import type { ArgumentTypes } from 'types/utility';

const pendingLazySelectorImports = new Set<Promise<unknown>>();

/**
 * Awaits every `lazySelector` import that is still in flight. Only the test
 * setup calls this.
 *
 * `lazySelector` fires its `import()` at module-evaluation time and never
 * awaits it, so nothing keeps the import inside the test file that triggered
 * it. Vitest tears the environment down when a file finishes; an import that
 * settles after that fails the run with `EnvironmentTeardownError`, blamed on
 * whichever unrelated suite happened to be next. Draining in a global
 * `afterAll` keeps each import inside its own file.
 */
export async function settleLazySelectorImports(): Promise<void> {
  // Loading one module can register further lazy selectors, so keep going
  // until the set stays empty.
  while (pendingLazySelectorImports.size > 0) {
    await Promise.allSettled([...pendingLazySelectorImports]);
  }
}

/**
 * Prevent circular dependency issues
 */
export function lazySelector<T extends (...args: any) => any>(
  importPromise: Promise<T>
): (...args: ArgumentTypes<T>) => ReturnType<T> | null {
  let ref: T | null = null;
  const loaded = importPromise.then(m => {
    ref = m;
  });

  // The import() is fired at module-evaluation time and never awaited. Under
  // test it can settle after the Vitest environment is torn down once a test
  // file finishes — that race is expected noise, so swallow it. Note this also
  // swallows a genuine module-load failure during a test run; it surfaces as
  // the selector returning null rather than an explicit error. Anywhere else
  // (dev/prod browser) a rejection means a genuine module/chunk load failure,
  // so rethrow to keep it surfacing as an unhandled rejection.
  loaded.catch(err => {
    if (process.env.NODE_ENV !== 'test') {
      throw err;
    }
  });

  // Separate branch so the bookkeeping never swallows the rethrow above.
  const tracked = loaded.then(
    () => undefined,
    () => undefined
  );
  pendingLazySelectorImports.add(tracked);
  void tracked.then(() => pendingLazySelectorImports.delete(tracked));

  return (...args) => {
    return ref ? ref(...args) : null;
  };
}
